import React from "react";
import { X, RotateCcw } from "lucide-react";
import { ZONE_KINDS, DEFAULT_ZONE_STYLES } from "@/lib/zoneStyles";

const MODES = [
  { v: "fill", label: "Remplissage" },
  { v: "outline", label: "Contour" },
  { v: "both", label: "Les deux" },
];

export default function ZoneStyleEditor({ kind, zoneStyles, onChange, onClose }) {
  const meta = ZONE_KINDS.find((k) => k.key === kind);
  const s = zoneStyles[kind] || DEFAULT_ZONE_STYLES[kind];
  const um = zoneStyles.unmitigated || DEFAULT_ZONE_STYLES.unmitigated;

  const patch = (p) => onChange({ ...zoneStyles, [kind]: { ...s, ...p } });
  const patchUm = (p) => onChange({ ...zoneStyles, unmitigated: { ...um, ...p } });
  const reset = () => onChange({ ...zoneStyles, [kind]: { ...DEFAULT_ZONE_STYLES[kind] } });

  return (
    <div className="absolute right-full top-0 mr-1 z-[70] panel p-3 w-64 space-y-3" data-testid={`zone-style-editor-${kind}`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-white/60 truncate">⚙️ {meta?.label || kind}</div>
        <button onClick={onClose} className="text-white/50 hover:text-white shrink-0" data-testid={`zone-style-close-${kind}`}><X size={13} /></button>
      </div>

      <div>
        <div className="text-[10px] text-white/40 uppercase tracking-widest mb-1">Rendu</div>
        <div className="grid grid-cols-3 gap-1">
          {MODES.map((m) => (
            <button
              key={m.v}
              onClick={() => patch({ mode: m.v })}
              className={`text-[10px] py-1 rounded-md border ${s.mode === m.v ? "border-accent-blue bg-accent-blue/15 text-accent-blue" : "border-white/10 hover:border-white/30"}`}
              data-testid={`zone-mode-${kind}-${m.v}`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between text-[10px] text-white/40 uppercase tracking-widest mb-1">
          <span>Opacité remplissage</span>
          <span className="font-mono text-white/70">{Math.round(s.opacity * 100)}%</span>
        </div>
        <input
          type="range" min="0.04" max="0.5" step="0.02"
          value={s.opacity}
          onChange={(e) => patch({ opacity: parseFloat(e.target.value) })}
          className="w-full accent-blue-500"
          data-testid={`zone-opacity-${kind}`}
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label className="flex items-center gap-2 text-[10px] text-white/60">
          <input type="color" value={s.bull} onChange={(e) => patch({ bull: e.target.value })}
            className="w-6 h-6 rounded cursor-pointer bg-transparent border border-white/10" data-testid={`zone-color-bull-${kind}`} />
          Haussier
        </label>
        <label className="flex items-center gap-2 text-[10px] text-white/60">
          <input type="color" value={s.bear} onChange={(e) => patch({ bear: e.target.value })}
            className="w-6 h-6 rounded cursor-pointer bg-transparent border border-white/10" data-testid={`zone-color-bear-${kind}`} />
          Baissier
        </label>
      </div>

      <div className="pt-2 border-t border-white/5 space-y-1.5">
        <label className="flex items-center gap-2 text-[10px] text-white/70 cursor-pointer">
          <input
            type="checkbox"
            checked={um.priority}
            onChange={(e) => patchUm({ priority: e.target.checked })}
            className="accent-blue-500"
            data-testid="zone-unmitigated-priority"
          />
          Prioriser les zones non-mitigées ★
        </label>
        {um.priority && (
          <div className="grid grid-cols-2 gap-1 pl-5">
            <button
              onClick={() => patchUm({ action: "dim" })}
              className={`text-[10px] py-1 rounded-md border ${um.action === "dim" ? "border-accent-blue bg-accent-blue/15 text-accent-blue" : "border-white/10"}`}
              data-testid="zone-unmitigated-dim"
            >Estomper mitigées</button>
            <button
              onClick={() => patchUm({ action: "hide" })}
              className={`text-[10px] py-1 rounded-md border ${um.action === "hide" ? "border-accent-blue bg-accent-blue/15 text-accent-blue" : "border-white/10"}`}
              data-testid="zone-unmitigated-hide"
            >Masquer mitigées</button>
          </div>
        )}
        <div className="text-[9px] text-white/35 leading-relaxed pl-5">
          ★ = zone jamais retestée par le prix — là où les pros attendent.
        </div>
      </div>

      <button onClick={reset} className="w-full text-[10px] py-1 rounded-md border border-white/10 hover:border-white/30 flex items-center justify-center gap-1" data-testid={`zone-style-reset-${kind}`}>
        <RotateCcw size={10} /> Réinitialiser
      </button>
    </div>
  );
}
